import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import integrationsService from '../../services/integrationsService';
import legacyUapiService from '../../services/LegacyUapiService';
import IntegrationDesigner from '../IntegrationDesigner';
import ConfirmModal from './ConfirmModal';
import CredentialSelector, { joinOverrides, splitOverrides } from './CredentialSelector';
import MarketplaceSettingField from './MarketplaceSettingField';
import '../AddPaymentPorterWizard.css';   // .ppw-field / .ppw-label — the Config dialog's fields
import OxiTestModal from './OxiTestModal';
import McpTestModal from './McpTestModal';
import CrawlerTestModal from './CrawlerTestModal';
import MqQueueModal from './MqQueueModal';
import FileViewerModal from './FileViewerModal';
import Spinner from './Spinner';
import { graphWithBindings } from './implementationGraph';
import useRouteGrants from './useRouteGrants';
import { apiErrorMessage } from '../../utils/apiError';

// WHAT THIS SOURCE SPEAKS — asked of the MARKETPLACE, by id.
//
// This matched the adapter string `mcp-proxy` and was wrong twice over: the rule on this platform is
// that ids are the only way to reference data and text is display-only, and the adapter was EMPTY on
// this very marketplace, so an MCP source opened the OXI test and was offered a box of OPERA 5 XML.
//
// The node carries `marketplace_id`. That is the handle; `mcp_enabled` (V507) is the answer, exactly as
// IS_VENDOR (V504) decides which systems a vendor may hold credentials for.
const systemOf = (node, systems) => (node && node.marketplace_id != null)
    ? ((systems || []).find(s => String(s.id) === String(node.marketplace_id)) || null)
    : null;

const isMcpSource = (node, systems) => {
    const system = systemOf(node, systems);
    return !!system && system.mcp_enabled === 'Y';
};

// WHICH TEST SUITE A SOURCE OPENS.
//
// By the system's own key (SYSTEMURLPARAMETER), which is unique-indexed and is what the enqueue lambdas
// match on — never the adapter string or the display name. An MCP source is decided by its flag one
// level up; everything else is a message-and-answer suite, and the key says which messages.
//
// DEFAULTS TO THE OXI SUITE, as it always has: a system with no suite of its own is still tested by
// pushing a message at it, and offering nothing would be worse than offering the wrong samples.
// The Crawler is not a sending system: its test is a dry run of the site it reads (CrawlerTestModal).
const SUITE_BY_SYSTEM = { PEGASUS: 'pegasus', ORACLEOHIP: 'ohip', CRAWLER: 'crawler' };
const testSuiteFor = (node, systems) => {
    const system = systemOf(node, systems);
    return (system && SUITE_BY_SYSTEM[String(system.system_key || '').toUpperCase()]) || 'oxi';
};

// ONE IMPLEMENTATION OF A PIPELINE TEMPLATE, DRAWN — the template's board, read-only, with each slot filled from that
// property's bindings (v2_integration_implementation). Used by the template editor's Implementations tab
// (IntegrationForm) and by MCP as a Service's hotel Implementation tab (McpHotelImplementation).
//
// `graph` is the template's own (GET /pipelines/{id}/graph — nodes, routes, systems, customCode). The implementation,
// the property's own Universal API credentials and backend bindings are read here, so the board resolves names, logos
// and the x-hotelid for THAT property rather than for whichever hotel the navbar is on.
//
// THE SHAPE IS LOCKED, THE CREDENTIALS ARE NOT. Which systems, proxies and routes there are belongs to the template;
// which credentials fill them is this property's answer and, for a superadmin, is given right here. A change is a
// BINDING, never a template column — writing it to the node or the route would fill that slot for every property.
//
// ONE UNIVERSAL API CREDENTIAL PER IMPLEMENTATION (V649). Every route is made as impl.uapi_credential_id; it is set
// once on the board (above the first Universal API box, superadmin only) and each route chooses only its binding —
// the x-hotelid it lands through.
//
// `status` is the parent's copy (it changes status on its own endpoint), sent with every save so a binding change never
// puts back the status the board loaded with. `onSaved(implementation)` hears the server's answer.
//
// THE ACTIONS AN IMPLEMENTATION OFFERS ARE THE BOARD'S OWN — Test (the source's suite, made as this property), Deploy
// Streaming Client, Event poller, a polled target's queue and a storing target's files. They were wired by the template
// editor alone, so the same implementation drawn on MCP as a Service's hotel tab had no Test button. Here, every screen
// that draws an implementation offers the same ones. Test, streaming and the poller are superadmin-only, as they were.
export default function ImplementationBoard({
  templateId, implementationId, graph, status, isSuperAdmin, accountId: navAccountId, partnerId: navPartnerId, onSaved, onError, children,
}) {
  const id = templateId;   // the template, as the action endpoints and modals name it (/pipelines/{id}/…)
  // WHAT IS BEING TESTED — a source, AND the implementation it is being tested as.
  //
  // THE BUTTON USED TO SIT ON THE TEMPLATE'S System box, which was the wrong place the moment the
  // template stopped carrying credentials (V508): a template has no property, no credential and no
  // x-hotelid, so "test this" had nothing to make the call with and would have tested whatever the
  // node happened to still hold. A test is a question about one property's running pipeline, so it
  // belongs on the implementation that IS that pipeline.
  const [testSource, setTestSource] = useState(null);
  const closeTest = () => setTestSource(null);

  // A SOURCE'S CONFIG (V654) — its system's PIPELINES settings for this implementation (the Crawler's url and interval).
  // { node, data, draft, touched, error, saving } while the dialog is open. Only the settings typed in are sent, so a
  // PASSWORD left blank keeps what is stored; an emptied field removes this implementation's own value (an inheritable
  // setting then falls back to the marketplace's default).
  const [configFor, setConfigFor] = useState(null);
  const openConfig = async (node) => {
    setConfigFor({ node, data: null, draft: {}, touched: {}, error: null, saving: false });
    try {
      const data = await integrationsService.getSourceConfig(id, implementationId, node.id);
      setConfigFor(f => (f ? { ...f, data, draft: Object.fromEntries(data.settings.map(x => [x.name, x.value || ''])) } : f));
    } catch (e) {
      setConfigFor(f => (f ? { ...f, error: apiErrorMessage(e, 'The settings could not be read.') } : f));
    }
  };
  const saveConfig = async () => {
    const values = Object.fromEntries(Object.keys(configFor.touched).map(k => [k, configFor.draft[k] || '']));
    setConfigFor(f => ({ ...f, saving: true, error: null }));
    try {
      const data = await integrationsService.saveSourceConfig(id, implementationId, configFor.node.id, values);
      setConfigFor(f => (f ? { ...f, data, draft: Object.fromEntries(data.settings.map(x => [x.name, x.value || ''])), touched: {}, saving: false } : f));
    } catch (e) {
      setConfigFor(f => (f ? { ...f, saving: false, error: apiErrorMessage(e, 'The settings could not be saved.') } : f));
    }
  };
  // RUN NOW (V658) — start a source the hub runs on a schedule (the Crawler: one crawl), after a confirm.
  // { node, running, result, error } while the dialog is open; result is the server's { started, message }.
  // CREDENTIALS (marketplacesettings.TYPE = 'AUTH') — the source whose sign-in credential is being picked, or null.
  const [credentialFor, setCredentialFor] = useState(null);
  const [runNowFor, setRunNowFor] = useState(null);
  const runNow = async () => {
    setRunNowFor(f => ({ ...f, running: true, error: null }));
    try {
      const result = await integrationsService.runSourceNow(id, { implementationId, nodeId: runNowFor.node.id });
      setRunNowFor(f => (f ? { ...f, running: false, result } : f));
    } catch (e) {
      setRunNowFor(f => (f ? { ...f, running: false, error: apiErrorMessage(e, 'The source could not be started.') } : f));
    }
  };
  // THE QUEUE FOR A POLLED TARGET, and what a storing target holds (V583) — { route } while open.
  const [queueFor, setQueueFor] = useState(null);
  const [filesFor, setFilesFor] = useState(null);

  const [impl, setImpl] = useState(null);           // null while loading
  // THE PROPERTY'S OWN CHAIN AND PARTNER for every credential picker on this board — what it may pick from is decided by
  // the hotel it runs at, not by whatever chain the navbar happens to show (which found nothing for a hotel elsewhere).
  const accountId = (impl && impl.account_id) || navAccountId;
  const partnerId = (impl && impl.partner_id) || navPartnerId;
  const [credentials, setCredentials] = useState([]);
  const [access, setAccess] = useState([]);
  const [saving, setSaving] = useState(false);
  // Bumped after a target credential is bound, so the bindings are read again with it in them.
  const [accessRev, setAccessRev] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setImpl(null);
    integrationsService.getImplementation(templateId, implementationId)
      .then(res => { if (!cancelled) setImpl(res.implementation || null); })
      .catch(e => { if (!cancelled && onError) onError(apiErrorMessage(e, 'That implementation could not be opened.'), true); });
    return () => { cancelled = true; };
    // onError is the parent's; a new closure each render must not reload the implementation.
    // eslint-disable-next-line
  }, [templateId, implementationId]);

  const hotelId = impl && impl.hotel_id;
  useEffect(() => {
    let cancelled = false;
    if (!hotelId) { setCredentials([]); setAccess([]); return undefined; }
    legacyUapiService.listCredentials({ hotelId, perCredential: true })
      .then(res => {
        if (cancelled) return;
        setCredentials((res.data?.credentials || res.data || [])
          .filter(r => r.credential_id)
          .map(r => ({
            id: r.credential_id,
            credential_name: r.credential_name || r.hotelname || `Credential #${r.credential_id}`,
            hotel_id: r.hotel_id,
            backend_systems: (r.access_systems || '').split('|').filter(Boolean).join(', ') || null,
          })));
      })
      .catch(() => { if (!cancelled) setCredentials([]); });
    legacyUapiService.listAccess(hotelId)
      .then(res => { if (!cancelled) setAccess(res.data?.access || res.data || []); })
      .catch(() => { if (!cancelled) setAccess([]); });
    return () => { cancelled = true; };
  }, [hotelId, accessRev]);

  // The template's shape with this property's credentials in it — a NEW graph, never the template's own.
  const board = useMemo(() => (impl && graph ? graphWithBindings(graph, impl) : null), [graph, impl]);
  const routeGrants = useRouteGrants(board && board.routes);

  // THE WHOLE IMPLEMENTATION IS RE-SENT, because that is what the endpoint takes (wholesale, so a cleared slot can be
  // expressed). The local copy is updated first so the board does not flicker back while the save is in flight, and
  // reverted if it fails.
  const save = useCallback(async (next) => {
    const before = impl;
    const { bindings } = next;
    setImpl(next);
    setSaving(true);
    if (onError) onError(null);
    try {
      const res = await integrationsService.saveImplementation(templateId, {
        implementationId: impl.id,
        hotelId: impl.hotel_id,
        name: impl.name,
        status: status || impl.status,
        notes: impl.notes,
        // ONE UNIVERSAL API CREDENTIAL FOR EVERY ROUTE (V649); the server writes it onto each route row.
        uapiCredentialId: next.uapi_credential_id || undefined,
        bindings: bindings.map(b => ({
          nodeId: b.node_id || undefined,
          routeId: b.route_id || undefined,
          credentialId: b.credential_id || undefined,
          uapiAccessId: b.uapi_access_id || undefined,
          hotelidOverride: b.hotelid_override || undefined,
          credentialOverrides: b.credential_overrides || undefined,
        })),
      });
      // The server's answer is authoritative — it resolves credential names and the x-hotelid.
      const saved = res.implementation || next;
      if (res.implementation) setImpl(res.implementation);
      if (onSaved) onSaved(saved);
    } catch (e) {
      setImpl(before);
      if (onError) onError(apiErrorMessage(e, 'That change could not be saved.'));
    } finally {
      setSaving(false);
    }
  }, [templateId, impl, status, onSaved, onError]);

  const saveBinding = useCallback((slot, patch) => {
    if (!impl) return;
    const key = slot.nodeId ? 'node_id' : 'route_id';
    const slotId = slot.nodeId || slot.routeId;
    const existing = (impl.bindings || []).find(b => String(b[key]) === String(slotId));
    const bindings = existing
      ? (impl.bindings || []).map(b => (b === existing ? { ...b, ...patch } : b))
      : [...(impl.bindings || []), { [key]: slotId, ...patch }];
    return save({ ...impl, bindings });
  }, [impl, save]);

  // THE PIPELINE'S CALLER CHANGED. A route's binding issued to another credential cannot be called as this one (the
  // Universal API refuses the pair), so it is cleared to be chosen again rather than refused by the save.
  const saveUapiCredential = useCallback((credentialId, knownName = null) => {
    if (!impl) return;
    const c = credentials.find(x => String(x.id) === String(credentialId)) || (knownName ? { credential_name: knownName } : null);
    const foreign = (b) => {
      const a = b.uapi_access_id && access.find(x => String(x.id) === String(b.uapi_access_id));
      return a && a.uapi_credential_id && String(a.uapi_credential_id) !== String(credentialId);
    };
    return save({
      ...impl,
      uapi_credential_id: credentialId,
      uapi_credential_name: c ? c.credential_name : null,
      bindings: (impl.bindings || []).map(b => (b.route_id
        ? { ...b, credential_id: credentialId, ...(foreign(b) ? { uapi_access_id: null, x_hotelid: null } : {}) }
        : b)),
    });
  }, [impl, credentials, access, save]);

  // GENERATE / REGENERATE THE UNIVERSAL API CREDENTIAL (the Credential chevron). Generate issues a new one for this
  // property and makes it the caller through the ordinary save; Regenerate — a new client id and secret on the one it
  // has, which stops anything holding the old secret — is confirmed first.
  const [issuing, setIssuing] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const issueUapiCredential = useCallback(async (mode) => {
    if (!impl) return;
    setIssuing(true);
    if (onError) onError(null);
    try {
      const res = await integrationsService.issueImplementationUapiCredential(templateId, impl.id, mode);
      if (mode === 'generate' && res.credentialId) {
        await saveUapiCredential(res.credentialId, res.name);
        setAccessRev(r => r + 1);   // the dropdown lists it
      }
    } catch (e) {
      if (onError) onError(apiErrorMessage(e, mode === 'regenerate'
        ? 'The credential could not be regenerated.' : 'A credential could not be issued.'));
    } finally {
      setIssuing(false);
    }
  }, [impl, templateId, onError, saveUapiCredential]);

  // THE SOURCE'S CREDENTIAL, from a dialog (Deploy Streaming Client): the node slot's binding, with its per-hotel values,
  // exactly as the System Credential on the board saves it.
  const saveNodeCredential = useCallback((nodeId, cred, values) => {
    const { hotelidOverride, credentialOverrides } = splitOverrides(values || {});
    return saveBinding({ nodeId }, {
      credential_id: cred ? cred.credential_id : null,
      credential_name: cred ? cred.credential_name : null,
      hotelid_override: cred ? hotelidOverride : null,
      credential_overrides: cred ? credentialOverrides : null,
    });
  }, [saveBinding]);

  // THE TARGET'S CREDENTIAL, picked from the target's Credential button (the shared picker). A route already bound
  // keeps its binding — and its x-hotelid — and the binding takes the credential; an unbound one is given a binding to
  // the system it is designed for, made as this implementation's Universal API credential, which mints its x-hotelid.
  // The binding is the property's (uapi_hotel_access), so another route on the same binding signs in with it too.
  // `values` are the per-property overrides typed beside it — the hotel id in its own column (hotelid_override), the
  // rest as credential_overrides, as the Universal API screen stores them.
  const pickTargetCredential = useCallback(async (route, picked, values = {}) => {
    const credentialId = picked && (picked.credential_id || picked.id);
    if (!impl || !credentialId) return;
    const hotelidOverride = values.HOTELID || values.HOTEL_ID || null;
    const rest = Object.fromEntries(Object.entries(values).filter(([k, v]) => !['HOTELID', 'HOTEL_ID'].includes(k) && v));
    const credentialOverrides = Object.keys(rest).length ? rest : null;
    const bound = route.uapi_access_id && access.find(a => String(a.id) === String(route.uapi_access_id));
    setSaving(true);
    if (onError) onError(null);
    try {
      if (bound) {
        await legacyUapiService.updateAccess(bound.id, { credentialId, hotelidOverride, credentialOverrides });
        setAccessRev(r => r + 1);
        setSaving(false);
        return;
      }
      const res = await legacyUapiService.createAccess({
        hotelId: impl.hotel_id,
        marketplaceId: route.target_marketplace_id,
        credentialId,
        hotelidOverride,
        credentialOverrides,
        uapiCredentialId: impl.uapi_credential_id || undefined,
      });
      const created = res.data || {};
      setAccessRev(r => r + 1);
      setSaving(false);
      saveBinding({ routeId: route.id }, { uapi_access_id: created.id, x_hotelid: created.uapi_hotel_id });
    } catch (e) {
      setSaving(false);
      if (onError) onError(apiErrorMessage(e, 'That credential could not be bound to the route.'));
    }
  }, [impl, access, onError, saveBinding]);

  if (!board) return <div className="loading-section"><Spinner /></div>;
  return (
    <>
      <IntegrationDesigner
        nodes={board.nodes}
        systems={graph.systems}
        routes={board.routes || []}
        customCode={graph.customCode || []}
        credentials={credentials}
        routeGrants={routeGrants}
        access={access}
        hotelId={impl.hotel_id}
        accountId={accountId}
        partnerId={partnerId}
        isSuperAdmin={isSuperAdmin}
        readOnly
        credentialsReadOnly={!isSuperAdmin || saving}
        readOnlyReason={saving ? 'Saving…' : 'Only a superadmin can change the credentials on an implementation.'}
        uapiCredential={{
          id: impl.uapi_credential_id, name: impl.uapi_credential_name, onChange: saveUapiCredential, issuing,
          onIssue: (mode) => (mode === 'regenerate' ? setConfirmRegenerate(true) : issueUapiCredential('generate')),
        }}
        onPickTargetCredential={pickTargetCredential}
        // EVERY FIELD THE BOARD CAN CHANGE IS A BINDING COLUMN. Listed rather than spread, so a patch this table cannot
        // hold is a visible omission here instead of a value silently dropped on the way to the server.
        onUpdateNode={(nodeId, patch) => saveBinding({ nodeId }, {
          ...('credential_id' in patch ? { credential_id: patch.credential_id, credential_name: patch.credential_name } : {}),
          ...('hotelid_override' in patch ? { hotelid_override: patch.hotelid_override } : {}),
          ...('credential_overrides' in patch ? { credential_overrides: patch.credential_overrides } : {}),
        })}
        // A route's own answer is only its binding now; its caller is the implementation's (uapiCredential).
        onUpdateRoute={(route, patch) => saveBinding({ routeId: route.id },
          'uapi_access_id' in patch ? { uapi_access_id: patch.uapi_access_id } : {})}
        onViewQueue={(route) => setQueueFor({ route, implementation: impl })}
        onViewFiles={(route) => setFilesFor({ route, implementation: impl })}
        onTestSource={isSuperAdmin ? (node) => setTestSource(node) : undefined}
        onSourceConfig={isSuperAdmin ? (node) => openConfig(node) : undefined}
        onSourceRunNow={isSuperAdmin ? (node) => setRunNowFor({ node, running: false, result: null, error: null }) : undefined}
        onSourceCredential={isSuperAdmin ? (node) => setCredentialFor(node) : undefined}
      />
      {children}
      {/* WHICH TEST DEPENDS ON WHAT THE SOURCE SPEAKS. An OXI source is tested by sending it a
          message; an MCP server is tested by running the sequence a client runs — discovery, the
          challenge, the handshake, the catalogue, a call. One button, because to the person it is
          the same question; two suites, because they share no step. */}
      {/* DEPLOY STREAMING CLIENT. The implementation's streaming consumer (containers/ohip-events on EKS):
          its live status, the settings it runs with, Deploy / Redeploy and its log. The deploy goes through
          this pipeline's API (which checks the implementation is this template's) to the same streaming-clients
          lambda Admin › Infra › Streaming Clients uses, where it can also be stopped. */}
      {/* In a portal: the board sits inside the template page's <form>, and ConfirmModal's buttons would submit it. */}
      {confirmRegenerate && createPortal(
        <ConfirmModal
          title="Regenerate the Universal API credential"
          message={`${impl.uapi_credential_name || 'This credential'} is given a new client id and secret. This pipeline uses `
            + 'the new ones at once; anything else holding the old secret can no longer get a token once its current one '
            + 'expires. Its app key, enterprise id and external system stay the same.'}
          confirmLabel="Regenerate"
          danger
          onConfirm={() => { setConfirmRegenerate(false); issueUapiCredential('regenerate'); }}
          onCancel={() => setConfirmRegenerate(false)}
        />,
        document.body,
      )}
      {/* CONFIG (V654). The source system's PIPELINES settings, as this property runs them. Saved through this
          pipeline's API, audited there (setting names, never a PASSWORD's value). */}
      {configFor && (
        <ConfirmModal
          title={`${configFor.node.system_name || 'Source'} config`}
          confirmLabel={configFor.saving ? 'Saving…' : 'Save'}
          confirmDisabled={configFor.saving || !configFor.data || !Object.keys(configFor.touched).length}
          onConfirm={saveConfig}
          cancelLabel="Close"
          closeOnBackdrop={false}
          onCancel={() => setConfigFor(null)}
        >
          <p className="cm-message">
            What <strong>{configFor.node.adapter || configFor.node.system_name || 'this source'}</strong> runs with for{' '}
            <strong>{impl.hotelname || impl.name}</strong>. A field left empty uses the system&rsquo;s default, where it has one.
          </p>
          {configFor.error && <div className="os-msg os-msg-error">{configFor.error}</div>}
          {!configFor.data && !configFor.error && <p className="cm-message">Loading…</p>}
          {configFor.data && configFor.data.settings.map(x => (
            <div className="ppw-field" key={x.name}>
              <label className="ppw-label" htmlFor={`cfg-${x.name}`}>
                {x.label || x.name} {x.required === 'Y' && <span className="ppw-required">*</span>}
              </label>
              <MarketplaceSettingField
                id={`cfg-${x.name}`}
                def={x}
                value={configFor.draft[x.name]}
                disabled={configFor.saving || x.locked === 'Y'}
                placeholder={String(x.dataType || '').toUpperCase() === 'PASSWORD' && x.isSet
                  ? 'Set — type to replace'
                  : (x.defaultValue ? `Default: ${x.defaultValue}` : (x.placeholder || ''))}
                onChange={(v) => setConfigFor(f => ({ ...f, draft: { ...f.draft, [x.name]: v }, touched: { ...f.touched, [x.name]: true } }))}
              />
              {x.helper && <span className="ppw-label-hint">{x.helper}</span>}
            </div>
          ))}
          {configFor.data && !configFor.data.complete && (
            <p className="cm-message">Some required settings have no value and no default yet.</p>
          )}
        </ConfirmModal>
      )}
      {/* RUN NOW (V658). Confirmed first — a crawl reads the property's website and writes its content. Then the
          server's answer: started, or already running (never started twice). Audited as a RUN by the pipelines API. */}
      {runNowFor && (
        <ConfirmModal
          title={`Run ${runNowFor.node.system_name || 'source'} now`}
          confirmLabel={runNowFor.running ? 'Starting…' : 'Run now'}
          confirmDisabled={runNowFor.running}
          hideConfirm={!!runNowFor.result}
          onConfirm={runNow}
          cancelLabel={runNowFor.result ? 'Close' : 'Cancel'}
          onCancel={() => setRunNowFor(null)}
        >
          {!runNowFor.result && (
            <p className="cm-message">
              Start <strong>{runNowFor.node.adapter || runNowFor.node.system_name || 'this source'}</strong> for{' '}
              <strong>{impl.hotelname || impl.name}</strong> now, rather than waiting for its schedule.
            </p>
          )}
          {runNowFor.error && <div className="os-msg os-msg-error">{runNowFor.error}</div>}
          {runNowFor.result && (
            <div className={`os-msg ${runNowFor.result.started ? 'os-msg-success' : ''}`}>{runNowFor.result.message}</div>
          )}
        </ConfirmModal>
      )}
      {/* CREDENTIALS. The credential this implementation's source adapter signs in to its system with, and its
          per-property values (the OHIP hotel id), picked with the shared picker and saved on the node's binding — the
          same save as the streaming client's credential. Read from the board so a change shows at once. */}
      {credentialFor && (() => {
        const node = (board.nodes || []).find(n => String(n.id) === String(credentialFor.id)) || credentialFor;
        // STRAIGHT TO THE LIST, as a target's Credential opens it — not a dialog whose only content was "Add Credential",
        // which read as "make a new one" and hid the credentials already shared with this property.
        return (
          <CredentialSelector
            screenOnly
            open
            onOpenChange={(v) => { if (!v) setCredentialFor(null); }}
            marketplaceId={node.marketplace_id}
            hotelId={impl.hotel_id}
            accountId={accountId}
            partnerId={partnerId}
            canEdit={isSuperAdmin}
            title={`${node.system_name || 'Source'} credential`}
            subtitle={`What ${node.adapter || node.system_name || 'this source'} signs in to ${node.system_name || 'the system'} with, for this property.`}
            value={node.credential_id ? { credential_id: node.credential_id, credential_name: node.credential_name } : null}
            overridesInPicker
            overrides={joinOverrides(node.hotelid_override, node.credential_overrides)}
            overridesTitle="For this property"
            disabled={saving}
            onChange={(cred, values) => { if (cred) saveNodeCredential(node.id, cred, values); }}
          />
        );
      })()}
      {testSource && testSuiteFor(testSource, graph.systems) === 'crawler' ? (
        <CrawlerTestModal integrationId={id} implementation={impl} source={testSource} onClose={closeTest} />
      ) : testSource && isMcpSource(testSource, graph.systems) ? (
        <McpTestModal
          isOpen
          onClose={closeTest}
          implementation={impl}
          integrationId={id}
          source={testSource}
        />
      ) : (
        <OxiTestModal
          isOpen={!!testSource}
          onClose={closeTest}
          implementation={impl}
          integrationId={id}
          source={testSource}
          suite={testSuiteFor(testSource, graph.systems)}
        />
      )}

      {/* WHAT A POLLED TARGET HAS NOT COLLECTED. There is no response to read for a target that
          is never called, so this is where "did it arrive?" is answered. */}
      {queueFor && (
        <MqQueueModal
          isOpen
          onClose={() => setQueueFor(null)}
          integrationId={id}
          accessId={queueFor.route.uapi_access_id}
          systemName={queueFor.route.backend_systems
            || queueFor.route.target_system_name
            || 'this target'}
          propertyName={queueFor.implementation.hotelname || queueFor.implementation.name}
        />
      )}

      {/* WHAT A STORING TARGET HOLDS (V583). Its answer is "stored", so the files are the result. */}
      {filesFor && (
        <FileViewerModal
          isOpen
          onClose={() => setFilesFor(null)}
          integrationId={id}
          accessId={filesFor.route.uapi_access_id}
          systemName={filesFor.route.backend_systems
            || filesFor.route.target_system_name
            || 'this target'}
          propertyName={filesFor.implementation.hotelname || filesFor.implementation.name}
        />
      )}
    </>
  );
}
